<template>

  <div v-if="error.length" class="error">
    Please correct the following errors:
    <ul>
      <li v-for="(err, index) in error" :key="index">{{ err }}</li>
    </ul>
  </div>


  <div class="cell small-12">
      <h1>Direct Debit Mandate</h1>
      <p>We require customers to complete a direct debit mandate to allow us to process billing of our services.</p>
    </div>

    <div class="cell small-12">
      <form @submit.prevent>
        <div id="signatories-significant-control">
        <label v-for="(contact, idx) in localContacts" :key="idx" class="radio-label custom-check">
          <input type="radio" :value="'Person ' + idx" name="ddsignatory" />
          {{ contact.firstName + " " + contact.lastName }} 
          <span class="radiomark"></span>
        </label>
        </div>
      </form>
    </div>


    <div class="cell small-8">
      <form @submit.prevent>
      <br/>
      <label class="custom-check">
             <input type="checkbox" v-model="isCustomSignatory">The direct debit signatory is not listed, I want to provide their details
            <span class="checkmark"></span>
        </label>
        <br/>
        <div v-if="isCustomSignatory" class="custom-signatory-fields">
          <label>
            Forename
            <input type="text" v-model="customSignatoryObject.forename" />
          </label>
          <label>
            Surname
            <input type="text" v-model="customSignatoryObject.surname" />
          </label>
          <label>
            Job Title
            <input type="text" v-model="customSignatoryObject.jobTitle" />
          </label>
          <label>
            Email Address
            <input type="email" v-model="customSignatoryObject.email" />
          </label>
        </div>
        </form>
    </div>


</template>


<script setup>

import { ref, watch, defineEmits, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router';
import { useIdentityStore } from '@/stores/identity'

const error = ref([])
const route = useRoute();
const signupId = route.params.signupId;
const identity = useIdentityStore()

const isCustomSignatory = ref(false)
const customSignatoryObject = reactive({
  forename: '',
  surname: '',
  jobTitle: '',
  email: '',
  isVerificationRequired: false
})

const props = defineProps({
  formData: Object,
  clientInvite: Object
})

const localContacts = ref([]);

onMounted(() => {
  localContacts.value = props.formData.contacts ? [...props.formData.contacts] : [];
});


watch(isCustomSignatory, (newValue) => {
  const radioButtons = document.querySelectorAll('#signatories-significant-control input[type="radio"]');
  radioButtons.forEach(radio => {
    radio.disabled = newValue;
    radio.checked = false;
  });
});

const validateStep = () => {
  error.value = [];

  if (isCustomSignatory.value) {
    const firstName = customSignatoryObject.forename?.trim() || '';
    const lastName = customSignatoryObject.surname?.trim() || '';
    const jobTitle = customSignatoryObject.jobTitle?.trim() || '';
    const email = customSignatoryObject.email?.trim() || '';

    if (!firstName) {
      error.value.push('First name is required.');
    } else if (firstName.length > 50) {
      error.value.push('First name cannot exceed 50 characters.');
    }

    if (!lastName) {
      error.value.push('Last name is required.');
    } else if (lastName.length > 50) {
      error.value.push('Last name cannot exceed 50 characters.');
    }

    if (!jobTitle) {
      error.value.push('Job title is required.');
    } else if (jobTitle.length > 50) {
      error.value.push('Job title cannot exceed 50 characters.');
    }

    if (!email) {
      error.value.push('Email address is required.');
    } else if (email.length > 256) {
      error.value.push('Email address cannot exceed 256 characters.');
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailPattern.test(email)) {
        error.value.push('Please enter a valid email address.');
      }
    }

    return error.value.length === 0;
  }

  const radios = document.querySelectorAll(
    '#signatories-significant-control input[type="radio"]'
  );

  const checked = Array.from(radios).some(radio => radio.checked);

  if (!checked) {
    error.value.push('Please select a direct debit signatory.');
    return false;
  }

  return true;
};

import { postClientSignup } from '@/services/clientsignupmanager.func';

const emit = defineEmits(['update-form']);
const executeStep = async () => {
  let client = { ...props.formData }

  client.phoneNumber = (client.phoneNumber || '')
  .trim()
  .replace(/[\s()-]/g, '')

  client = signatoryMapper(client);
  client = currentUserMapper(client);

  var request = {
    client: client,
    signupDeadlineDate: props.clientInvite.signupDeadline
  }

  const res = await postClientSignup(request, signupId);
  if (res.status !== 202) {
    error.value.push('Sorry, we\'ve been unable to create your account, please contact your Qtac account manager.');
    return false;
  }

  return true;
}

const signatoryMapper = (formData) => {
  let mappedData = { ...formData };

  if (isCustomSignatory.value) {
    const customContact = {
      firstName: customSignatoryObject.forename,
      lastName: customSignatoryObject.surname,
      jobTitle: customSignatoryObject.jobTitle,
      email: customSignatoryObject.email,
      isVerificationRequired: false,
      isSlaSignatory: false,
      isDirectDebitAuthoriser: true,
    };

    mappedData.contacts = [
      ...(formData.contacts || []).map(contact => ({
        ...contact,
        isDirectDebitAuthoriser: false,
      })),
      customContact,
    ];
  } else {
    const radios = document.querySelectorAll(
      '#signatories-significant-control input[type="radio"]'
    );

    let selectedIdx = null;

    radios.forEach((radio, idx) => {
      if (radio.checked) {
        selectedIdx = idx;
      }
    });

    if (
      selectedIdx !== null &&
      formData.contacts &&
      formData.contacts[selectedIdx]
    ) {
      mappedData.contacts = formData.contacts.map((contact, index) => ({
        ...contact,
        isDirectDebitAuthoriser: index === selectedIdx,
      }));
    }
  }

  return mappedData;
};

const currentUserMapper = (formData) => {
  const email = identity.account?.username;
  const name = identity.account?.name;

  if (!email) {
    return formData;
  }

  const contacts = formData.contacts || [];

  const existingContactIndex = contacts.findIndex(
    contact => contact.email?.toLowerCase() === email.toLowerCase()
  );

  let updatedContacts;

  if (existingContactIndex !== -1) {
    updatedContacts = contacts.map((contact, index) =>
      index === existingContactIndex
        ? { ...contact, isNotifiable: true }
        : contact
    );
  } else {
    // Remove anything in brackets and trim whitespace
    const cleanName = name?.replace(/\s*\(.*?\)\s*$/, '').trim() || '';

    // Split the remaining name
    const nameParts = cleanName.split(/\s+/);

    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const currentUserContact = {
      firstName,
      lastName,
      email,
      isNotifiable: true,
    };

    updatedContacts = [...contacts, currentUserContact];
  }

  return {
    ...formData,
    contacts: updatedContacts,
  };
};


defineExpose({ validateStep, executeStep });

</script>